import React, { useEffect, useState } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { useFormik } from "formik";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import {
  TempleServiceEdit,
  TempleServiceDetails,
} from "../../services/temple-service.services";
import { TempleList } from "../../services/temple.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import Select from "react-select";

const ServiceEditTemple = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [submitted, setSubmitted] = useState(false);
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);

  const breadcrumbs = [
    { title: t("Dashboard"), url: "/admin/dashboard" },
    { title: t("Temple"), url: "/admin/temples/list/1" },
    { title: t("Service"), url: "/admin/temple-service/list/1" },
    { title: t("Edit"), url: "" },
  ];

  useEffect(() => {
    globalLoader(true);

    TempleList({}).then((res) => {
      setTemples(res?.data?.list || []);
    });

    TempleServiceDetails(id)
      .then((res) => {
        if (res?.success) {
          const service = res.data;
          formik.setValues({
            templeId: service.templeId?._id || "",
            name: service.name,
            serviceType: service.serviceType,
            duration: service.duration,
            description: service.description,
            price: service.price,
          });
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(res),
            ...SWAL_SETTINGS,
          });
        }
      })
      .finally(() => {
        setLoading(false);
        globalLoader(false);
      });
  }, [id]);

  const validationSchema = Yup.object({
    templeId: Yup.string()
      .required("Temple is required"),
    name: Yup.string()
      .required("Service name is required")
      .trim("Service name cannot be empty"),
    serviceType: Yup.string()
      .required("Service type is required")
      .trim("Service type cannot be empty"),
    duration: Yup.string()
      .required("Duration is required")
      .trim("Duration cannot be empty"),
    price: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required")
      .min(10, "Price must be at least 10")
      .test(
        "no-space",
        "Price cannot contain spaces",
        (value) => value !== undefined && !/\s/.test(String(value))
      ),
    description: Yup.string()
      .required("Description is required")
      .trim("Description cannot be empty"),
  });


const formik = useFormik({
  initialValues: {
    templeId: "",
    name: "",
    serviceType: "",
    duration: "",
    description: "",
    price: "",
  },
  enableReinitialize: true,
  validationSchema,
  onSubmit: (values) => {
    globalLoader(true);
    setSubmitted(true);

    const formData = new FormData();
    formData.append("o_id", id); // service id
    formData.append("templeId", values.templeId);
    formData.append("name", values.name);
    formData.append("serviceType", values.serviceType);
    formData.append("duration", values.duration);
    formData.append("description", values.description);
    formData.append("price", values.price);

    TempleServiceEdit(formData)
      .then((res) => {
        globalLoader(false);
        setSubmitted(false);
        if (res.success) {
          Swal.fire({
            icon: "success",
            text: res.message || "Service updated successfully",
            ...SWAL_SETTINGS,
          });
          navigate("/admin/temple-service/list/1");
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(res) || "Something went wrong",
            ...SWAL_SETTINGS,
          });
        }
      })
      .catch((err) => {
        console.log("Error editing service:", err);
        globalLoader(false);
        setSubmitted(false);
        Swal.fire({
          icon: "error",
          text:
            err?.response?.data?.message ||
            err?.message ||
            "Server error, please try again",
          ...SWAL_SETTINGS,
        });
      });
  },
});


  const templeOptions = temples.map((t) => ({
    value: t._id,
    label: `${t.templeName} (${t.templeLocation})`,
  }));

  const durationOptions = [10, 15, 20, 30, 45, 60, 90, 120, 150, 180];
  const formatDurationLabel = (minutes) =>
    minutes < 60
      ? `${minutes} Minute`
      : `${minutes} Minute - ${minutes / 60} Hour`;

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="card custom-card">
          <div className="card-body">
            <h6 className="main-content-label mb-3">
              {t("Edit Temple Service")}
            </h6>

            {/* Temple Select */}
            <div className="form-group">
              <label>
                {t("Temple")} <span className="requirestar">*</span>
              </label>
                <Select
                  options={templeOptions}
                  placeholder="Search & select temple..."
                  isSearchable={false}       
                  value={
                    templeOptions.find(
                      (opt) => opt.value === formik.values.templeId,
                    ) || null
                  }
                  onChange={(selected) =>
                    formik.setFieldValue("templeId", selected?.value || "")
                  }
                  isDisabled={true} 
                />
              <span className="text-danger d-flex text-left">
                <CustomError name="templeId" form={formik} />
              </span>
            </div>

            {/* Name */}
            <div className="form-group">
              <label>
                Service Name <span className="requirestar">*</span>
              </label>
              <input
                className="form-control"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
              />
              <span className="text-danger d-flex text-left">
                <CustomError name="name" form={formik} />
              </span>
            </div>

            {/* Service Type */}
            <div className="form-group">
              <label>
                Service Type <span className="requirestar">*</span>
              </label>
              <input
                className="form-control"
                name="serviceType"
                value={formik.values.serviceType}
                onChange={formik.handleChange}
              />
              <span className="text-danger d-flex text-left">
                <CustomError name="serviceType" form={formik} />
              </span>
            </div>

            {/* Duration */}
            <div className="form-group">
              <label>
                Duration <span className="requirestar">*</span>
              </label>
              <select
                name="duration"
                className="form-control"
                value={formik.values.duration}
                onChange={(e) =>
                  formik.setFieldValue("duration", Number(e.target.value))
                }
              >
                <option value="">Select Duration</option>
                {durationOptions.map((min) => (
                  <option key={min} value={min}>
                    {formatDurationLabel(min)}
                  </option>
                ))}
              </select>
              <span className="text-danger d-flex text-left">
                <CustomError name="duration" form={formik} />
              </span>
            </div>

            {/* Price */}
            <div className="form-group">
              <label>
                Price <span className="requirestar">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                name="price"
                value={formik.values.price}
                onChange={formik.handleChange}
              />
              <span className="text-danger d-flex text-left">
                <CustomError name="price" form={formik} />
              </span>
            </div>

            {/* Description */}
            <div className="form-group">
              <label>
                Description <span className="requirestar">*</span>
              </label>
              <textarea
                className="form-control"
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
              />
              <span className="text-danger d-flex text-left">
                <CustomError name="description" form={formik} />
              </span>
            </div>

            {/* Buttons */}
            <button
              className="btn btn-info me-2"
              type="submit"
              disabled={submitted}
            >
              Submit
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ServiceEditTemple;
